import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import PageHero from '../../components/feature/PageHero';
import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const steps = [
  {
    number: '01',
    icon: 'ri-user-search-line',
    title: 'Análise do Perfil',
    description: 'Entendemos suas necessidades específicas, histórico de sinistros e perfil de risco para estruturar a melhor estratégia.',
    color: 'orange'
  },
  {
    number: '02',
    icon: 'ri-file-search-line',
    title: 'Diagnóstico Técnico',
    description: 'Avaliamos apólices atuais, identificamos gaps de cobertura e analisamos cláusulas críticas que podem impactar sua proteção.',
    color: 'green'
  },
  {
    number: '03',
    icon: 'ri-shield-check-line',
    title: 'Estruturação de Coberturas',
    description: 'Dimensionamos corretamente capitais segurados, franquias e coberturas adicionais alinhadas ao seu risco real.',
    color: 'orange'
  },
  {
    number: '04',
    icon: 'ri-team-line',
    title: 'Negociação Estratégica',
    description: 'Utilizamos nosso relacionamento com seguradoras para obter as melhores condições técnicas e comerciais do mercado.',
    color: 'green'
  },
  {
    number: '05',
    icon: 'ri-file-list-3-line',
    title: 'Implementação',
    description: 'Acompanhamos todo o processo de contratação, garantindo que todas as condições negociadas sejam cumpridas.',
    color: 'orange'
  },
  {
    number: '06',
    icon: 'ri-customer-service-2-line',
    title: 'Acompanhamento Contínuo',
    description: 'Monitoramos sua apólice durante toda a vigência, realizamos revisões periódicas e oferecemos suporte em sinistros.',
    color: 'green'
  }
];

const benefits = [
  {
    icon: 'ri-shield-star-line',
    title: 'Proteção Adequada',
    description: 'Coberturas dimensionadas corretamente para seu perfil de risco, evitando surpresas em sinistros.'
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Economia Inteligente',
    description: 'Redução de custos através de negociação técnica sem comprometer a qualidade da proteção.'
  },
  {
    icon: 'ri-time-line',
    title: 'Agilidade',
    description: 'Processos otimizados e relacionamento direto com seguradoras para resolução rápida.'
  },
  {
    icon: 'ri-eye-line',
    title: 'Transparência',
    description: 'Análise detalhada de todas as condições contratuais com explicações claras e acessíveis.'
  }
];

export default function ComoFuncionaPage() {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Como Funciona Nossa Consultoria em Seguros",
    "description": "Entenda nosso processo de consultoria em seguros empresariais: análise técnica, diagnóstico, implementação e acompanhamento contínuo.",
    "url": "https://ibxbrasil.com.br/como-funciona"
  };

  return (
    <>
      <SEOHead
        title="Como Funciona a Consultoria em Seguros | IBX Brasil"
        description="Conheça nosso processo de consultoria: análise técnica das apólices, diagnóstico de riscos, implementação de melhorias e acompanhamento contínuo da sua empresa."
        canonical="https://ibxbrasil.com.br/como-funciona"
        ogImage="https://readdy.ai/api/search-image?query=step%20by%20step%20insurance%20consulting%20process%20diagram%20with%20professional%20team%20analyzing%20business%20insurance%20policies%20and%20implementing%20solutions&width=1200&height=630&seq=ibx-como-funciona-og&orientation=landscape"
        schema={webPageSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="COMO FUNCIONA"
          title="Processo Estruturado para Contratação Profissional"
          highlightedWord="Profissional"
          highlightColor="green"
          subtitle="Nossa metodologia combina análise técnica, negociação estratégica e acompanhamento contínuo para garantir a melhor proteção com economia inteligente."
          benefits={[
            'Processo transparente em 6 etapas',
            'Análise técnica detalhada de riscos',
            'Negociação direta com seguradoras',
            'Acompanhamento durante toda a vigência'
          ]}
          ctaText="Iniciar Minha Análise"
          ctaLink="/#contato"
          ctaSecondary={{
            text: 'Falar com Especialista',
            link: 'https://wa.me/5577988080505'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20insurance%20consultant%20explaining%20process%20workflow%20to%20client%20using%20tablet%20and%20documents%20in%20modern%20bright%20office%20with%20step%20by%20step%20methodology%20charts%20representing%20structured%20approach%20and%20transparency%20in%20consulting%20services&width=1920&height=800&seq=como-funciona-hero-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Process Steps */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Nosso Processo em 6 Etapas
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Metodologia estruturada para garantir proteção patrimonial com economia e transparência
              </p>
            </div>

            <div className="space-y-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row gap-8 items-center ${
                    index % 2 === 1 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Content */}
                  <div className="flex-1">
                    <div className="bg-white border border-gray-200 rounded-lg p-8 hover:border-orange-500 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-start gap-4 mb-4">
                        <span className="text-5xl font-bold text-gray-200">
                          {step.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {step.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className={`w-24 h-24 bg-${step.color}-100 rounded-full flex items-center justify-center`}>
                      <i className={`${step.icon} text-5xl text-${step.color}-600`}></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Benefícios do Nosso Processo
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Resultados concretos através de gestão profissional de seguros
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${benefit.icon} text-4xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Quanto Tempo Leva?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Prazos médios para cada etapa do processo
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">24-48h</h3>
                <p className="text-gray-600 mb-4">Análise Inicial</p>
                <p className="text-sm text-gray-500">
                  Primeiro contato, entendimento de necessidades e diagnóstico preliminar
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">5-7 dias</h3>
                <p className="text-gray-600 mb-4">Estruturação e Negociação</p>
                <p className="text-sm text-gray-500">
                  Análise técnica completa, estruturação de coberturas e negociação com seguradoras
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">2-3 dias</h3>
                <p className="text-gray-600 mb-4">Implementação</p>
                <p className="text-sm text-gray-500">
                  Formalização da contratação e início do acompanhamento contínuo
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-lg text-gray-600 mb-6">
                <strong className="text-gray-900">Total médio:</strong> 7 a 12 dias úteis do primeiro contato até a apólice ativa
              </p>
              <p className="text-sm text-gray-500 max-w-2xl mx-auto">
                * Os prazos podem variar conforme complexidade do risco, número de seguradoras consultadas e documentação necessária
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Perguntas Frequentes
              </h2>
              <p className="text-xl text-gray-600">
                Dúvidas comuns sobre nosso processo
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Preciso cancelar meu seguro atual para iniciar?
                </h3>
                <p className="text-gray-600">
                  Não. Podemos analisar sua apólice atual e estruturar a melhor estratégia para a próxima renovação, sem necessidade de cancelamento imediato.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Há algum custo pela análise inicial?
                </h3>
                <p className="text-gray-600">
                  A análise inicial é gratuita e sem compromisso. Você só contrata se aprovar nossa proposta técnica e comercial.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Como funciona o acompanhamento contínuo?
                </h3>
                <p className="text-gray-600">
                  Monitoramos sua apólice durante toda a vigência, realizamos revisões periódicas, oferecemos suporte em sinistros e preparamos a renovação com antecedência.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Vocês trabalham com quais seguradoras?
                </h3>
                <p className="text-gray-600">
                  Temos relacionamento com as principais seguradoras do mercado brasileiro, o que nos permite comparar condições e negociar as melhores propostas para cada perfil.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Pronto para Iniciar Sua Análise Gratuita?
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Entre em contato agora e descubra como podemos proteger seu patrimônio com economia inteligente
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Falar no WhatsApp</span>
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Análise Gratuita
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <a
                href="/servicos"
                className="inline-flex items-center justify-center px-8 py-4 bg-action-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Conhecer Nossos Serviços
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}